import { formatLocale, formatNumber } from '../format/locale-format';

/**
 * The two dates the header carries. Both describe the reader's own day: the Gregorian one comes
 * from `Intl`, the Hijri one from the app's single Umm al-Qura adapter, which hands over three
 * numbers for this file to word.
 */
export interface JourneyDates {
  readonly gregorian: string;
  /** Empty when no Hijri date could be worked out: the Gregorian one then stands alone. */
  readonly hijri: string;
}

/** Day, month and year as the calendar counts them — month is 1-based, as `Intl` reports it. */
export interface HijriParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

interface DateFormatter {
  format(date: Date): string;
}

export type DateFormatterFactory = (
  locale: string,
  options: Intl.DateTimeFormatOptions,
) => DateFormatter;

const intlFactory: DateFormatterFactory = (locale, options) =>
  new Intl.DateTimeFormat(locale, options);

/** The digits of the language being read: Eastern Arabic in Arabic, 0-9 elsewhere. */
const latinDigits = formatLocale;

/** Locales lowercase the weekday; the header opens on it, so it reads better capitalised. */
const capitaliseFirst = (text: string): string =>
  text.length === 0 ? text : text[0]!.toLocaleUpperCase() + text.slice(1);

export const formatGregorian = (
  instant: Date,
  language: string,
  timeZone?: string,
  createFormatter: DateFormatterFactory = intlFactory,
): string =>
  capitaliseFirst(
    createFormatter(latinDigits(language), {
      ...(timeZone === undefined ? {} : { timeZone }),
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(instant),
  );

/**
 * A day named on its own — "28 septembre 2026" — for the dates the app reports back rather than
 * the one it opens on: those need the year, and no weekday, because they are already past.
 */
export const formatCalendarDay = (
  instant: Date,
  language: string,
  timeZone?: string,
  createFormatter: DateFormatterFactory = intlFactory,
): string =>
  createFormatter(latinDigits(language), {
    ...(timeZone === undefined ? {} : { timeZone }),
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(instant);

/**
 * "15 Rabīʿ al-Ākhir 1448", with the month named by the active catalogue rather than by `Intl`,
 * and no era suffix: the calendar is named by its months, not by an abbreviation.
 *
 * Returns nothing when the month is out of range or unnamed, so a catalogue that has not been
 * filled in yet leaves the Gregorian date alone rather than printing a gap.
 */
export const formatHijri = (
  parts: HijriParts,
  monthNames: readonly string[],
  language = 'fr',
): string => {
  const name = monthNames[parts.month - 1];
  if (name === undefined || name.length === 0) return '';
  const digits = (value: number): string => formatNumber(value, language, { useGrouping: false });
  return `${digits(parts.day)} ${name} ${digits(parts.year)}`;
};

/**
 * The calendar day an instant falls on where the phone is, as YYYY-MM-DD: printed at half past
 * midnight in Brussels, a will is dated that day, not the day before as UTC would have it.
 */
export const localCalendarDay = (instant: Date): string =>
  [
    String(instant.getFullYear()).padStart(4, '0'),
    String(instant.getMonth() + 1).padStart(2, '0'),
    String(instant.getDate()).padStart(2, '0'),
  ].join('-');
